import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    const getApiUrl = (endpoint) => {
        if (process.env.REACT_APP_BACKEND_URL) {
            return `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
        }
        if (typeof window !== "undefined" && window.location) {
            return `${window.location.origin}${endpoint}`;
        }
        return endpoint;
    };

    useEffect(() => {
        // Check for stored auth with tenant hostname boundary
        const currentHost = typeof window !== "undefined" && window.location ? window.location.hostname : "";
        const storedHost = localStorage.getItem("auth_host");

        if (storedHost && currentHost && storedHost !== currentHost) {
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            localStorage.removeItem("auth_host");
        } else {
            const storedUser = localStorage.getItem("user");
            const storedToken = localStorage.getItem("token");
            if (storedUser && storedToken) {
                try {
                    setUser(JSON.parse(storedUser));
                    setToken(storedToken);
                } catch (_) {
                    localStorage.removeItem("user");
                    localStorage.removeItem("token");
                }
            }
        }
        setLoading(false);
    }, []);

    const login = async (email, password) => {
        try {
            const response = await axios.post(
                getApiUrl("/api/auth/login"),
                {
                    email,
                    password,
                },
            );

            if (response.data.success) {
                if (response.data.require_password_change) {
                    return { success: true, requirePasswordChange: true, email: response.data.email };
                }

                setUser(response.data.user);
                setToken(response.data.token);
                localStorage.setItem(
                    "user",
                    JSON.stringify(response.data.user),
                );
                localStorage.setItem("token", response.data.token);
                if (response.data.tenant) {
                    const t = response.data.tenant;
                    localStorage.setItem("tenant_branding", JSON.stringify({
                        slug: t.slug,
                        displayName: t.display_name,
                        brandName: t.brand_name || t.display_name || "Traveego",
                        logoUrl: t.logo_url || "/traveego-logo.jpeg"
                    }));
                }
                if (currentHost) {
                    localStorage.setItem("auth_host", currentHost);
                }
                return { success: true };
            }
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || "Login failed",
            };
        }
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("auth_host");
    };

    return (
        <AuthContext.Provider value={{ user, setUser, token, setToken, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};
